import { useCallback } from 'react';
import { useAuth } from './AuthProvider.js';
import { getSupabase } from '../supabase.js';

/**
 * Restituisce un resolver async di business_id per il tenant corrente.
 *
 * Why: alcuni flussi richiedono `business_id` non-null (cleaning_products,
 * cleaning_equipments, cleaning_schedules — colonna NOT NULL nel DB). Se il
 * JWT claim `active_business_id` non è popolato (es. dopo un bootstrap appena
 * fatto, prima che la session sia rinfrescata), il save fallisce silenziosamente
 * con "Nessun locale attivo". Con questo helper, prima fallback su una query
 * `businesses` per `tenant_id` e prendiamo il primo: l'utente reale ha sempre
 * almeno un business per tenant attivo.
 */
export function useActiveBusiness(): {
  resolve: () => Promise<{ tenantId: string; businessId: string } | null>;
} {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const knownBusinessId = profile?.active_business_id ?? null;

  const resolve = useCallback(async () => {
    if (!tenantId) return null;
    if (knownBusinessId) return { tenantId, businessId: knownBusinessId };
    // Fallback: query DB per il primo business del tenant
    const { data, error } = await getSupabase()
      .from('businesses')
      .select('id')
      .eq('tenant_id', tenantId)
      .is('archived_at', null)
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    const businessId = (data as { id: string }).id;
    return { tenantId, businessId };
  }, [tenantId, knownBusinessId]);

  return { resolve };
}
